import { FORM_ERROR, Form, getDRFFormError, useFormState, type FormData, type SubmissionErrors } from '@ff-it/form';
import type { RequestFailure } from '@ff-it/api';
import { Button, Dialog, DialogContent, DialogFooter, DialogTitle } from '@ff-it/ui';
import type { ReactNode } from 'react';
import { actionErrorOrThrow, formatError } from 'utilities';
import * as styles from './DiscountRuleDialog.css';

export type DiscountRuleDialogShellProps<Values extends FormData> = {
  open: boolean;
  title: ReactNode;
  initialValues: Values;
  validate: (values: Values) => Record<string | symbol, string>;
  onSubmit: (values: Values) => Promise<SubmissionErrors | void>;
  onRemove?: () => Promise<void>;
  onClose: () => void;
  removeLabel?: string;
  saveLabel?: string;
  children: ReactNode;
};

type DialogFormContentProps = {
  onRemove?: () => Promise<void>;
  onClose: () => void;
  removeLabel: string;
  saveLabel: string;
  children: ReactNode;
};

function DialogFormContent({ onRemove, onClose, removeLabel, saveLabel, children }: DialogFormContentProps) {
  const { hasValidationErrors, submitting } = useFormState({
    subscription: { hasValidationErrors: true, submitting: true },
  });

  const handleRemove = async () => {
    if (!onRemove) return;
    try {
      await onRemove();
    } catch (error) {
      actionErrorOrThrow(error as RequestFailure<unknown>);
    }
  };

  return (
    <>
      {children}
      <DialogFooter mt="2">
        <Button variant="secondary" disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        {onRemove && (
          <Button type="button" variant="danger" ml="1" disabled={submitting} onClick={handleRemove}>
            {removeLabel}
          </Button>
        )}
        <Button variant="primary" type="submit" ml="auto" disabled={hasValidationErrors || submitting}>
          {saveLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

/** Shared dialog/form lifecycle for provider and client rule editors. */
export function DiscountRuleDialogShell<Values extends FormData>({
  open,
  title,
  initialValues,
  validate,
  onSubmit,
  onRemove,
  onClose,
  removeLabel = 'Delete rule',
  saveLabel = 'Save rule',
  children,
}: DiscountRuleDialogShellProps<Values>) {
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className={styles.dialog} p="4">
        <DialogTitle>{title}</DialogTitle>
        <Form<Values>
          initialValues={initialValues}
          noValidate
          validate={validate}
          onSubmit={async (values) => {
            try {
              return await onSubmit(values);
            } catch (error) {
              return (
                getDRFFormError(error as RequestFailure<unknown>) ?? {
                  [FORM_ERROR]: formatError(error) || 'Unable to save this rule.',
                }
              );
            }
          }}
        >
          <DialogFormContent onRemove={onRemove} onClose={onClose} removeLabel={removeLabel} saveLabel={saveLabel}>
            {children}
          </DialogFormContent>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
